import { gql } from '@apollo/client';
import { Button, HStack, useDisclosure } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import NextLink from 'next/link';
import * as React from 'react';

import {
  MembershipOrderVariant,
  MembershipTermPaymentAlert,
  MembershipTermPaymentStatus,
  MembershipTermRenewalSetting,
} from '@/graphql-types/globalTypes';
import { MembershipTermLifecycleStatus } from '@/graphql-types/globalTypes';
import { CreditCardIcon } from '@/imports/ui/chakra/feather';

import { MembershipActionButtons_MembershipTermDataFragment } from './graphql-types/MembershipActionButtons_MembershipTermDataFragment';

import { CancelNextMembershipTerm } from './components/CancelNextMembershipTerm';
import { ExtensionModal } from './components/ExtensionModal';
import { TerminationModal } from './components/TerminationModal';

export const MembershipActionButtons: React.FC<{
  membershipTerm: MembershipActionButtons_MembershipTermDataFragment;
  displayName: string;
  hasNextTerm: boolean;
  shouldBeAdminView: boolean;
  canCurrentUserManageOrganizationProfile: boolean;
}> & { membershipTermDataFragment: DocumentNode } = ({
  membershipTerm,
  displayName,
  hasNextTerm,
  shouldBeAdminView,
  canCurrentUserManageOrganizationProfile,
}) => {
  const isVerificationNeeded =
    membershipTerm.paymentAlert ===
    MembershipTermPaymentAlert.ACH_VERIFICATION_NEEDED;
  const isFailedPaymentOnTerm =
    membershipTerm.paymentAlert === MembershipTermPaymentAlert.PAYMENT_FAILED;
  const isTermSetToAutopay =
    membershipTerm.renewalSetting ===
    MembershipTermRenewalSetting.AUTOMATIC_RENEWAL;
  const isTermPaid =
    membershipTerm.paymentStatus === MembershipTermPaymentStatus.PAID;
  const isFirstOrderOfRenewal =
    membershipTerm.paymentOrder &&
    (membershipTerm.paymentOrder.variant === MembershipOrderVariant.FIRST ||
      membershipTerm.paymentOrder.variant === MembershipOrderVariant.RECURRING);
  const isMonthly = !!membershipTerm.paymentsRemaining;

  const membershipTermPaymentAlertsThatCannotReceivePayment = [
    MembershipTermPaymentAlert.ACH_PROCESSING,
    MembershipTermPaymentAlert.ACH_VERIFICATION_NEEDED,
  ];

  const canUserReceivePayment =
    !isTermPaid &&
    (!membershipTerm.paymentAlert ||
      !membershipTermPaymentAlertsThatCannotReceivePayment.includes(
        membershipTerm.paymentAlert
      )) &&
    membershipTerm.paymentOrder?._id &&
    (!isTermSetToAutopay || isFailedPaymentOnTerm) &&
    (!isMonthly || isFirstOrderOfRenewal || isFailedPaymentOnTerm);

  const canUserUpdatePayment =
    membershipTerm.paymentAlert === MembershipTermPaymentAlert.PAYMENT_FAILED &&
    membershipTerm.paymentOrder?._id;

  const extensionModal = useDisclosure();
  const terminationModal = useDisclosure();

  const canUserCancel =
    membershipTerm.lifecycleStatus === MembershipTermLifecycleStatus.IMPENDING;

  const showActionButtons =
    isVerificationNeeded ||
    membershipTerm.parentMembershipTerm?.owner.__typename === 'Organization' ||
    canUserCancel ||
    canUserUpdatePayment ||
    canUserReceivePayment ||
    (shouldBeAdminView && membershipTerm.canCurrentUserExtendEndDate) ||
    (shouldBeAdminView && membershipTerm.canCurrentUserTerminate);

  if (!showActionButtons) {
    return null;
  }

  return (
    <HStack>
      {membershipTerm.parentMembershipTerm?.owner.__typename ===
        'Organization' && (
        <NextLink
          href={
            shouldBeAdminView
              ? `/admin/companies/${membershipTerm.parentMembershipTerm.owner._id}/details`
              : canCurrentUserManageOrganizationProfile
              ? '/my-company/dashboard/overview'
              : `/companies/${membershipTerm.parentMembershipTerm.owner._id}`
          }
        >
          <Button variant="outline" size="sm" colorScheme="gray">
            View Parent Company
          </Button>
        </NextLink>
      )}

      {canUserCancel && (
        <CancelNextMembershipTerm nextMembershipTermId={membershipTerm._id} />
      )}

      {canUserUpdatePayment && (
        <NextLink
          href={
            shouldBeAdminView
              ? `/admin/membership-orders/${membershipTerm.paymentOrder?._id}/receive-payment`
              : `/membership-orders/${membershipTerm.paymentOrder?._id}/pay`
          }
        >
          <Button variant="outline" size="sm" colorScheme="gray">
            Update Payment
          </Button>
        </NextLink>
      )}

      {canUserReceivePayment && (
        <NextLink
          href={
            shouldBeAdminView
              ? `/admin/membership-orders/${membershipTerm.paymentOrder?._id}/receive-payment`
              : `/membership-orders/${membershipTerm.paymentOrder?._id}/pay`
          }
        >
          <Button
            variant="outline"
            size="sm"
            colorScheme="gray"
            leftIcon={<CreditCardIcon />}
          >
            {shouldBeAdminView ? 'Receive Payment' : 'Pay Now'}
          </Button>
        </NextLink>
      )}

      {shouldBeAdminView && membershipTerm.canCurrentUserExtendEndDate && (
        <>
          <Button
            variant="outline"
            size="sm"
            colorScheme="gray"
            onClick={extensionModal.onOpen}
          >
            Extend
          </Button>

          {extensionModal.isOpen && (
            <ExtensionModal
              displayName={displayName}
              membershipTerm={membershipTerm}
              onClose={extensionModal.onClose}
              isOpen
            />
          )}
        </>
      )}

      {isVerificationNeeded &&
        membershipTerm.paymentOrder?.inFlightStripePaymentTransaction
          ?.paymentMethodObject?.__typename === 'PaymentMethod_UsBankAccount' &&
        membershipTerm.paymentOrder.inFlightStripePaymentTransaction
          .paymentMethodObject.verificationUrl && (
          <Button
            as="a"
            size="sm"
            target="_blank"
            rel="noopener noreferrer"
            href={
              membershipTerm.paymentOrder.inFlightStripePaymentTransaction
                .paymentMethodObject.verificationUrl
            }
            variant="outline"
            colorScheme="gray"
          >
            Verify
          </Button>
        )}

      {shouldBeAdminView && membershipTerm.canCurrentUserTerminate && (
        <>
          <Button
            variant="outline"
            size="sm"
            colorScheme="gray"
            onClick={terminationModal.onOpen}
          >
            Terminate
          </Button>

          {terminationModal.isOpen && (
            <TerminationModal
              displayName={displayName}
              membershipTerm={membershipTerm}
              onClose={terminationModal.onClose}
              isOpen
              hasNextTerm={hasNextTerm}
            />
          )}
        </>
      )}
    </HStack>
  );
};

MembershipActionButtons.membershipTermDataFragment = gql`
  fragment MembershipActionButtons_MembershipTermDataFragment on MembershipTermV2 {
    _id
    paymentAlert
    paymentOrder {
      _id
      variant
    }
    lifecycleStatus
    renewalSetting
    paymentStatus
    paymentsRemaining
    canCurrentUserExtendEndDate
    canCurrentUserChangeRenewal
    canCurrentUserTerminate
    canCurrentUserChangeRenewal
    parentMembershipTerm {
      _id
      owner {
        ... on Organization {
          _id
        }
      }
    }

    paymentOrder {
      _id
      price
      paymentInterval
      inFlightStripePaymentTransaction {
        _id
        paymentMethodObject {
          _id
          ... on PaymentMethod_UsBankAccount {
            verificationUrl
          }
        }
      }
    }

    ...ExtensionModal_MembershipTermDataFragment
    ...TerminationModal_MembershipTermDataFragment
  }
  ${ExtensionModal.membershipTermDataFragment}
  ${TerminationModal.membershipTermDataFragment}
`;
